import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type {
  SignalWindowMetricRow,
  SignalMetric,
  SignalEventBreakdown,
} from "@/lib/api/assistant";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  commodityLabel,
  formatNum,
  formatPct,
  formatBarTs,
  formatWindow,
  humanizeKey,
  instrumentLabel,
} from "../utils";

const METRIC_LABEL: Record<SignalMetric, string> = {
  price_delta: "Price Δ",
  price_delta_pct: "Price Δ %",
  day_to_day_avg_change: "Avg Day Δ",
  max_price_gain: "Max Gain",
  max_price_loss: "Max Loss",
  avg_close: "Avg Close",
};

function metricValue(metric: SignalMetric, raw: string): string {
  if (metric === "price_delta_pct") return formatPct(raw);
  return formatNum(raw);
}

function MetricCell({
  metric,
  value,
  isPrimary,
}: {
  metric: SignalMetric;
  value: string;
  isPrimary: boolean;
}) {
  return (
    <div
      className={
        "rounded-md border p-3 " +
        (isPrimary ? "border-indigo-300 bg-indigo-50/60" : "border-slate-200 bg-white")
      }
    >
      <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center justify-between">
        <span>{METRIC_LABEL[metric] ?? humanizeKey(metric)}</span>
        {isPrimary && (
          <Badge variant="outline" className="border-indigo-300 text-indigo-700 bg-white text-[10px] px-1.5">
            primary
          </Badge>
        )}
      </div>
      <div className="text-xl font-semibold text-slate-900 mt-1 tabular-nums">
        {metricValue(metric, value)}
      </div>
    </div>
  );
}

function PerSignalBreakdown({ events, barDisplay }: { events: SignalEventBreakdown[]; barDisplay: SignalEventBreakdown["bar_display"] }) {
  if (events.length === 0) {
    return <div className="text-xs text-slate-500 italic">No events.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="text-xs w-full border-collapse">
        <thead>
          <tr className="bg-slate-100 text-slate-600 text-left">
            <th className="px-2 py-1 font-medium">Signal Date</th>
            <th className="px-2 py-1 font-medium">Score</th>
            <th className="px-2 py-1 font-medium">Window</th>
            <th className="px-2 py-1 font-medium">First Bar</th>
            <th className="px-2 py-1 font-medium">First Close</th>
            <th className="px-2 py-1 font-medium">Last Bar</th>
            <th className="px-2 py-1 font-medium">Last Close</th>
            {events[0] && Object.keys(events[0].metrics).map((m) => (
              <th key={m} className="px-2 py-1 font-medium">
                {METRIC_LABEL[m as SignalMetric] ?? humanizeKey(m)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((ev, i) => (
            <tr key={i} className="border-t border-slate-200">
              <td className="px-2 py-1 whitespace-nowrap">{formatBarTs(ev.signal_ts, barDisplay)}</td>
              <td className="px-2 py-1 tabular-nums">{formatNum(ev.signal_value, 1)}</td>
              <td className="px-2 py-1 whitespace-nowrap text-slate-600">
                {formatBarTs(ev.window.start_ts, barDisplay)} → {formatBarTs(ev.window.end_ts, barDisplay)}
              </td>
              <td className="px-2 py-1 whitespace-nowrap">{formatBarTs(ev.first_bar_ts, barDisplay)}</td>
              <td className="px-2 py-1 tabular-nums">{formatNum(ev.first_bar_close)}</td>
              <td className="px-2 py-1 whitespace-nowrap">{formatBarTs(ev.last_bar_ts, barDisplay)}</td>
              <td className="px-2 py-1 tabular-nums">{formatNum(ev.last_bar_close)}</td>
              {Object.entries(ev.metrics).map(([m, v]) => (
                <td key={m} className="px-2 py-1 tabular-nums">
                  {metricValue(m as SignalMetric, v ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({ row }: { row: SignalWindowMetricRow }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const metrics = Object.keys(row.metrics) as SignalMetric[];
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            {commodityLabel(row.commodity_id)}
            <Badge
              variant="outline"
              className={`text-[10px] font-semibold ${
                row.signal_type === "buy"
                  ? "border-emerald-400 text-emerald-700 bg-emerald-50"
                  : "border-red-400 text-red-700 bg-red-50"
              }`}
            >
              {row.signal_type === "buy" ? "Buy" : "Sell"}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              {row.signal_count} signal{row.signal_count === 1 ? "" : "s"}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              aggregate: {row.aggregate}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              fwd window: {row.metric_window.count} {row.metric_window.window_type}
            </Badge>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{instrumentLabel(row.instrument)}</div>
        </div>
        <div className="text-xs text-slate-500 text-right">
          <div>Search: {formatWindow(row.search_window)}</div>
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {metrics.map((m) => (
          <MetricCell
            key={m}
            metric={m}
            value={row.metrics[m] ?? ""}
            isPrimary={m === row.primary_metric}
          />
        ))}
        {metrics.length === 0 && (
          <div className="text-sm text-slate-500 italic col-span-full">No metrics returned.</div>
        )}
      </div>
      {row.per_signal_breakdown.length > 0 && (
        <div className="border-t border-slate-200">
          <button
            type="button"
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="w-full px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2"
          >
            {showBreakdown ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Per-signal breakdown ({row.per_signal_breakdown.length})
          </button>
          {showBreakdown && (
            <div className="px-4 pb-4">
              <PerSignalBreakdown events={row.per_signal_breakdown} barDisplay={row.bar_display} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SignalWindowMetricResult({ rows }: { rows: SignalWindowMetricRow[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Signal Window Metric</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 && (
          <div className="text-sm text-slate-500 italic">No rows returned.</div>
        )}
        {rows.map((row, i) => (
          <Row key={`${row.commodity_id}-${i}`} row={row} />
        ))}
      </CardContent>
    </Card>
  );
}
